<template>
  <ul class="list module-list flex items-start pl-0">
    <div class="flex flex-col all-expander-container">
      <button
        class="expander expand-all-modules text-xs bg-grey-light my-0"
        :title="
          expandedModules.length > 0
            ? 'Collapse all modules'
            : 'Expand all modules'
        "
        @click="expandAllModules"
      >
        {{ expandedModules.length > 0 ? "▼" : "►" }}T
      </button>
      <button
        class="expander expand-all-topics text-xs bg-grey-light mt-2 mb-0"
        :title="
          expandedTopics.length > 0
            ? 'Collapse all topics'
            : 'Expand all topics'
        "
        @click="expandAllTopics"
      >
        {{ expandedTopics.length > 0 ? "▼" : "►" }}Q
      </button>
    </div>
    <ul class="list-reset ml-6">
      <li
        v-for="module in items"
        :key="module.id"
        class="list-item cursor-pointer"
      >
        <div
          class="expand-container flex justify-start items-center"
          @click="toggleModuleExpand(module.id)"
        >
          <button
            class="font-bold expander  my-0 bg-transparent"
            :class="{ expanded: moduleExpanded(module.id) }"
          >
            {{ moduleExpanded(module.id) ? "▼" : "►" }}
          </button>
          <div class="name text-lg truncate">
            {{ module.name }}
          </div>
        </div>
        <ul v-if="moduleExpanded(module.id)" class="list topic-list">
          <draggable
            :list="module.topics"
            :group="{ name: 'topic', pull: 'clone', put: false }"
            ghost-class="ghost"
            :sort="false"
          >
            <li
              v-for="topic in module.topics"
              :id="topic.id"
              :key="topic.id"
              class="list-item cursor-move"
              :class="{ expanded: topicExpanded(topic.id) }"
              itemtype="topic"
              @drag="handleDragStart(topic.questions)"
            >
              <div
                class="expand-container flex justify-start items-center mb-1"
                @click="toggleTopicExpand(topic.id)"
              >
                <button
                  class="font-bold expander  my-0 bg-transparent"
                  :class="{ expanded: topicExpanded(topic.id) }"
                >
                  {{ topicExpanded(topic.id) ? "▼" : "►" }}
                </button>
                <div class="name text-base truncate">
                  {{ topic.name }}
                </div>
              </div>
              <ul v-if="topicExpanded(topic.id)" class="list question-list">
                <draggable
                  :list="topic.questions"
                  :group="{ name: 'question', pull: 'clone', put: false }"
                  ghost-class="ghost"
                  :sort="false"
                >
                  <li
                    v-for="(question, questionIdx) in topic.questions"
                    :id="question.item.id"
                    :key="question.item.id"
                    class="list-item cursor-default question text-sm mb-2"
                    :class="{ selected: questionSelected(question.item.id) }"
                    :title="question.name"
                    itemtype="question"
                    :style="
                      questionSelectedLocally(question.item.id)
                        ? 'outline: 2px solid #3cb7d5;outline-offset: 1px;background: #3cb7d5;'
                        : ''
                    "
                    @click="
                      handleQuestionClick(
                        $event,
                        question,
                        questionIdx,
                        topic.questions,
                        topic.id
                      )
                    "
                    @drag="handleQuestionDragStart(question.item.id)"
                  >
                    <span class="flex items-center">
                      <i class="material-icons question-icon text-base">{{
                        iconToDisplay(question.item.type)
                      }}</i>
                      <i
                        class="material-icons question-flag question-icon text-lg mx-1  my-0"
                        :class="{ 'text-yellow': question.context.is_bonus }"
                        >{{
                          question.context.is_required
                            ? "playlist_add_check"
                            : question.context.is_bonus
                            ? "grade"
                            : ""
                        }}</i
                      >

                      <span class="ml-1">
                        {{ question.name }}
                      </span>
                    </span>
                  </li>
                </draggable>
              </ul>
            </li>
          </draggable>
        </ul>
      </li>
    </ul>
  </ul>
</template>

<script>
import draggable from "vuedraggable"

export default {
  components: { draggable },
  props: {
    items: {
      type: Array,
      required: true,
    },
    selectedQuestionIds: {
      type: Array,
      required: false,
      default() {
        return []
      },
    },
  },
  data() {
    return {
      expandedModules: [],
      expandedTopics: [],
      locallySelectedQuestions: [],
      firstSelectedQuestion: null,
    }
  },
  computed: {
    icons() {
      return {
        0: "question_answer",
        1: "block",
        2: "assignment",
        3: "insert_drive_file",
        4: "video_library",
        5: "games",
        6: "code",
        7: "camera_alt",
        8: "casino",
        9: "web",
        12: "assignment",
      }
    },
  },
  methods: {
    handleQuestionClick(evt, question, questionIdx, questions, topicId) {
      // select the question on click
      if (!evt.ctrlKey && !evt.shiftKey) {
        this.locallySelectedQuestions = [question]
        this.firstSelectedQuestion = { topicId, questionIdx }
        return
      }
      // select or deselect question if ctrl key is active even when range is selected
      if (evt.ctrlKey) {
        if (!this.questionSelectedLocally(question.item.id)) {
          this.locallySelectedQuestions.push(question)
        } else {
          this.locallySelectedQuestions = this.locallySelectedQuestions.filter(
            q => q.item.id !== question.item.id
          )
        }
        return
      }
      // select a range from first selected question to any direction
      if (evt.shiftKey) {
        if (
          this.firstSelectedQuestion &&
          this.firstSelectedQuestion.topicId === topicId
        ) {
          if (this.locallySelectedQuestions.length === 0) {
            this.locallySelectedQuestions = [question]
          } else {
            /* to allow selection in both ways
              swap start and end index with min and max */
            const startIdx = Math.min(
              this.firstSelectedQuestion.questionIdx,
              questionIdx
            )
            const endIdx = Math.max(
              this.firstSelectedQuestion.questionIdx,
              questionIdx
            )
            this.locallySelectedQuestions = questions.slice(
              startIdx,
              endIdx + 1
            )
          }
        }
      }
    },
    questionSelectedLocally(id) {
      return this.locallySelectedQuestions.find(q => q.item.id === id)
    },
    handleQuestionDragStart(id) {
      // only send questions if any of the selected questions are dragged
      if (
        this.locallySelectedQuestions.length > 0 &&
        this.questionSelectedLocally(id)
      ) {
        this.$emit("SELECTED_QUESTIONS_DRAGGED", this.locallySelectedQuestions)
      }
    },
    handleDragStart(questions) {
      // updated parent with the questions of dragged topic
      this.$emit("TOPIC_DRAGGED", questions)
    },
    iconToDisplay(type) {
      return this.icons[type] ? this.icons[type] : "report_problem"
    },
    questionSelected(id) {
      return this.selectedQuestionIds.includes(id)
    },
    moduleExpanded(id) {
      return this.expandedModules.find(item => item === id)
    },
    topicExpanded(id) {
      return this.expandedTopics.find(item => item === id)
    },
    toggleModuleExpand(id) {
      if (this.moduleExpanded(id)) {
        this.expandedModules = this.expandedModules.filter(mId => mId !== id)
      } else {
        this.expandedModules.push(id)
      }
    },
    toggleTopicExpand(id) {
      if (this.topicExpanded(id)) {
        this.expandedTopics = this.expandedTopics.filter(mId => mId !== id)
      } else {
        this.expandedTopics.push(id)
      }
    },
    expandAllModules() {
      if (this.expandedModules.length > 0) {
        this.expandedModules = []
        this.expandedTopics = []
        return
      }
      const mIds = []
      this.items.forEach(item => {
        mIds.push(item.id)
      })
      this.expandedModules = mIds
    },
    expandAllTopics() {
      if (this.expandedTopics.length > 0) {
        this.expandedTopics = []
        return
      }
      const mIds = []
      const tIds = []
      this.items.forEach(item => {
        item.topics.forEach(t => {
          mIds.push(item.id)
          tIds.push(t.id)
        })
      })
      this.expandedModules = mIds
      this.expandedTopics = tIds
    },
  },
}
</script>

<style scoped>
.list:not(.module-list),
.question-list {
  position: relative;
  padding-left: 1.5rem;
  list-style-type: none;
}

.topic-list,
.question-list {
  border-left: 1px solid silver;
}

.list-item {
  position: relative;
}

.list-item .name {
  position: relative;
  left: -1rem;
}

.expander {
  width: 1.8rem;
  padding: 0.5rem;
  border-radius: 3px;
}

.list-item .expander {
  position: relative;
  left: -1rem;
}

.expand-container {
  transition: background-color 0.1s ease-out;
}

.all-expander-container {
  position: sticky;
  top: 0rem;
}

.expand-container:hover,
.expand-container:hover .expander {
  background-color: #e0e0e0;
}

.expander:focus,
.expander:hover,
.expander:active {
  outline: none;
  color: inherit;
  background-color: #e0e0e0;
}

.expand-all-modules,
.expand-all-topics {
  width: 2.25rem;
  padding: 0.5rem;
}

.expand-all-topics {
  top: 3rem;
}

.question {
  padding: 0.4rem;
  border-radius: 3px;
  color: #fff;
  background: #6259c3;
  list-style-type: none;
  cursor: move;
}

.question:hover {
  background: #4a4392;
}

.selected {
  background-color: #38c172;
}

.selected:hover {
  background: #2c9056;
}

.question-name-move {
  transition: 0.3s;
}

.ghost {
  opacity: 0.5;
  color: #fff;
  background: #6259c3;
}

@keyframes dropped {
  0% {
    box-shadow: none;
  }
  100% {
    box-shadow: 0 0 0 10px #8080803d;
  }
}
</style>
